import { createAsyncThunk } from '@reduxjs/toolkit';
import Stardog from 'stardog';
import { isBrowserAuthConnection } from 'vet-bones/utils';

import {
  connectionAsyncActions,
  connectionsSlice,
} from 'src/store/connections/connections.slice';
import { Connection, ConnectionStatus } from 'src/store/connections/types';
import { upgradeConnection } from 'src/store/connections/upgradeConnection';
import { AppDispatch } from 'src/store/store';
import { ReduxState, ReduxStateGetter } from 'src/store/types';

export const setupPortalConnection =
  (
    connection: Connection,
    {
      connect = true,
      shouldNotNotifySuccess = false,
    }: {
      /**
       * Whether to try connecting immediately, or just setup connection in state.
       *
       * This will show a `SignInDialog` (asking for password) if token auth fails
       * (or no token was given).
       */
      connect?: boolean;
      /**
       * Whether to not display a notification when connection succeeds
       */
      shouldNotNotifySuccess?: boolean;
    } = {}
  ) =>
  async (dispatch: AppDispatch, getState: ReduxStateGetter) => {
    const { connection: currentConnection, status } = getState().connections;

    // Don't attempt to connect if we're already connected to the same connection
    if (
      currentConnection &&
      currentConnection.id === connection.id &&
      status === ConnectionStatus.SUCCESS
    ) {
      return;
    }

    if (connect) {
      if (connection.token || isBrowserAuthConnection(connection)) {
        await dispatch(
          connectToStardog({ connection, shouldNotNotifySuccess })
        );
      } else {
        // open SignInDialog instantly instead of trying and failing
        // due to an undefined token and password
        dispatch(connectionsSlice.actions.signedOut({ connection }));
      }
    } else {
      dispatch(
        connectionsSlice.actions.signedOut({ connection, showDialog: false })
      );
    }
  };

export const connectToStardog = createAsyncThunk<
  void,
  {
    connection: Connection;
    /** whether we should show a tooltip on successful connection */
    shouldNotNotifySuccess?: boolean;
  },
  { state: ReduxState }
>(
  'connectToStardog',
  async ({ connection, shouldNotNotifySuccess }, { dispatch, getState }) => {
    if (connection.username && !connection.token && !connection.password) {
      dispatch(connectionsSlice.actions.signedOut({ connection }));
      return;
    }
    dispatch(connectionsSlice.actions.connectToStardogStarted());

    // First check to see if the Stardog instance we are connecting to supports token
    // authentication. If so upgrade the connection details with a token provided by
    // the upgradeResponse. Otherwise continue as normal with the connection info unchanged.
    const newConnection = await upgradeConnection(connection);

    // do not show connection success notification when the create or publish project drawer is open
    const { isCreateProjectOverlayOpen, isPublishProjectDrawerOpen } =
      getState().ui;

    const validateResponse = await dispatch(
      connectionAsyncActions.requestValidateConnection({
        connection: newConnection,
        shouldNotNotifySuccess:
          shouldNotNotifySuccess ||
          isCreateProjectOverlayOpen ||
          isPublishProjectDrawerOpen,
      })
    );

    const succeeded =
      validateResponse.type ===
      connectionAsyncActions.requestValidateConnection.fulfilled.type;

    if (succeeded) {
      // A connection's stored `username` can disagree with whoever the token
      // authenticated as, aiming the permission lookup at someone else (403/404).
      const userResponse = await dispatch(
        connectionAsyncActions.requestCurrentUser({})
      );
      const authenticatedUsername =
        userResponse.type ===
        connectionAsyncActions.requestCurrentUser.fulfilled.type
          ? (userResponse.payload as Stardog.HTTP.Body)?.body
          : undefined;
      if (authenticatedUsername) {
        dispatch(
          connectionsSlice.actions.authenticatedUsernameResolved(
            authenticatedUsername
          )
        );
      }
      const username = authenticatedUsername || newConnection.username;

      const requests: Array<Promise<unknown>> = [
        dispatch(
          connectionAsyncActions.requestServerStatus({
            connection: newConnection,
            params: { databases: false },
          })
        ),
        dispatch(
          connectionAsyncActions.requestServerProperties({
            connection: newConnection,
            params: ['catalog.database', 'dbms.query.log.database'],
          })
        ),
      ];

      if (username) {
        requests.push(
          dispatch(
            connectionAsyncActions.requestCurrentPermissions({ username })
          )
        );
      } else {
        // `/admin/users/undefined` would only ever fail.
        dispatch(connectionsSlice.actions.permissionsLookupSkipped());
      }

      await Promise.all(requests);
    } else {
      dispatch(
        connectionsSlice.actions.signedOut({ connection: newConnection })
      );
    }

    dispatch(connectionsSlice.actions.connectToStardogFinished());
  }
);
